<script>
  import * as PIXI from 'pixi.js';
  import { onMount, afterUpdate } from 'svelte';
  import { Viewport } from 'pixi-viewport';
  import { drawBG, drawCursor } from './canvasUi' ;
  import { Stage, Layer } from '@pixi/layers';
  import LZString from 'lz-string';
  import { fileSave, fileOpen } from 'browser-fs-access';
	import { shortcut } from './shortcut.js';
	import { selected, currentBanner, currentBase, currentBlock, grid, drawingMode, showLayer, infoText, timelapseRunning, shiftDown, cDown, altDown, zDown } from './store.js';
  import { commandStore } from './commands';

  let canvasWidth; // size of our canvas element in pixels
  let canvasHeight; // size of our canvas element in pixels
  let worldWidth; // size of our grid in pixels
  let worldHeight; // size of our grid in pixels

  let app;
  let view; // reference to the canvas element
  let viewport; // pixi viewport acts as container for everything

  let backgroundLayer;
  let blockLayer;
  let bannerLayer;
  let uiLayer;

  let bannerTextures = [];
  let blockTextures = [];
  let cursorSprite;

  let canvasReady = false;
  let oldPos = {x: 0, y: 0, z:0, pattern: 1};

  let initialCommands = [];
  let compressedCommands;
  const url = new URL(window.location.toString());
  let urlParams;

  let copiedBanner = {
    block: null,
    base: null,
    patterns: {}
  }

  $: {
    worldWidth = $grid.width * 64;
    worldHeight = $grid.height * 64;
    if(backgroundLayer) {
      backgroundLayer.getChildByName('background').width = worldWidth;
      backgroundLayer.getChildByName('background').height = worldHeight;
    }
  }

  $: if (cursorSprite) {
    cursorSprite.position.x = $selected.x * 64;
    cursorSprite.position.y = $selected.y * 64;
  }

  $: $currentBanner = $grid.banners.get($selected.x + '|' + $selected.y + '|' + $selected.z) || null;

  $: $currentBase = $grid.banners.get($selected.x + '|' + $selected.y) || null;

  $: $currentBlock = $grid.banners.get('bl|' + $selected.x + '|' + $selected.y) || null;

  $: {
    if(bannerLayer) {
      bannerLayer.visible = $showLayer.banner;
    }
    if(blockLayer) {
      blockLayer.visible = $showLayer.block;
    }
  }

  /* 
  WHAT: Initialize & setup the canvas on page load 
  WHY: onMount fires once on page load so we don't need to do it any other time
  */
 
  onMount(() => {

    /* 
    WHAT: initialize & setup PIXI application
    WHY: PIXI.js is the framework we use for drawing things on canvas
    */
    app = new PIXI.Application({
      view,
      backgroundAlpha: 0,
      width: canvasWidth,
      height: canvasHeight,
      resolution: window.devicePixelRatio,
      antialias: true,
      resizeTo: view
    })

    /* 
    WHAT: use nearest neighbour when scaling
    WHY: our assets are pixelart & scaled up
    */
    PIXI.settings.SCALE_MODE = PIXI.SCALE_MODES.NEAREST;

    /* 
    WHAT: init Stage
    WHY: needed for pixi-layers to work
    */
    app.stage = new Stage();

    /* 
    WHAT: initialize & setup Viewport
    WHY: Pixi-Viewport gives us easy pan and zoom 
    */
    viewport = new Viewport({
        screenWidth: canvasWidth,
        screenHeight: canvasHeight,
        worldWidth: worldWidth,
        worldHeight: worldHeight,
        interaction: app.renderer.plugins.interaction // the interaction module is important for wheel to work properly when renderer.view is placed or scaled
    })
    app.stage.addChild(viewport)

    //Viewport options
    viewport
        .drag({
          keyToPress: "KeyZ",
        })
        .pinch()
        .wheel({
          keyToPress: "KeyZ",
        })


    /* 
    WHAT: Add layers to viewport
    WHY: Creating layers sets us up with correct z-indexing
    */
    backgroundLayer = viewport.addChild(new Layer());
    blockLayer = viewport.addChild(new Layer());
    bannerLayer = viewport.addChild(new Layer());
    bannerLayer.group.enableSort = true;
    uiLayer = viewport.addChild(new Layer());

    /* 
    Add background sprite to background layer
    */
    const backgroundTexture = drawBG(worldWidth, worldHeight);
    backgroundLayer.addChild(backgroundTexture);

    /* 
    Add cursor sprite to ui layer
    */
    cursorSprite = drawCursor();
    uiLayer.addChild(cursorSprite);

    /* 
    WHAT: Create an array of banner textures
    WHY: Banner textures can be put into a PIXI sprite by calling its index
    */
    //load spritesheet texture
    app.loader.add('spritesheet', './banners.json').add('blocksTexture', './blocks.png').add('blocks', './sortedblocks.json').load(onAssetsLoaded);

    function onAssetsLoaded(loader, resources) {
      // create an array to store the textures

      const baseTexture = resources.blocksTexture.texture

      for (let [key, value] of Object.entries(resources.spritesheet.data.frames)) {
        const texture = PIXI.Texture.from(key);
        const frame = value.frame;
        bannerTextures.push({ texture, frame });
      }
      for(let i = 0; i < resources.blocks.data.length; i++) {
        const frame = resources.blocks.data[i].frame;
        const rectangle = new PIXI.Rectangle(frame.x, frame.y, frame.w, frame.h);
        const blocktexture = new PIXI.Texture(baseTexture.baseTexture, rectangle);
        blockTextures.push({ blocktexture, frame });
      }
      //once banner texture has been loaded, the canvas is ready to function
      canvasReady = true;
    }
    /* 
    Center the canvas to middle and fit to canvas element size
    */
    viewport.fit();
    viewport.moveCenter(worldWidth / 2, worldHeight / 2);
  });

  /* 
  Show banner preview that follows the cursor after the banner textures have been loaded (canvasReady)
  */
  afterUpdate(() => {
    if(!canvasReady) return;
    drawPreview($selected);
  });

  $: if(oldPos.x !== $selected.x || oldPos.y !== $selected.y || oldPos.z !== $selected.z || oldPos.pattern !== $selected.pattern) {
    const prevBlock = blockLayer.getChildByName('bl|' + oldPos.x + '|' + oldPos.y)
    if(prevBlock !== null) {
      prevBlock.visible = true
    }
    if($drawingMode.block) {
      const currentBlock = blockLayer.getChildByName('bl|' + $selected.x + '|' + $selected.y)
      if(currentBlock !== null) {
        currentBlock.visible = false
      }
    }
    const prevBase = bannerLayer.getChildByName(oldPos.x + '|' + oldPos.y)
    if(prevBase !== null) {
      prevBase.visible = true
    }
    if($drawingMode.base) {
    const currentBase = bannerLayer.getChildByName($selected.x + '|' + $selected.y)
      if(currentBase !== null) {
        currentBase.visible = false
      }
    }
    const prevBanner = bannerLayer.getChildByName(oldPos.x + '|' + oldPos.y + '|' + oldPos.z)
    if(prevBanner !== null) {
      prevBanner.visible = true
    }
    if($drawingMode.pattern) {
      const currentBanner = bannerLayer.getChildByName($selected.x + '|' + $selected.y + '|' + $selected.z)
      if(currentBanner !== null) {
        currentBanner.visible = false
      }
    }
    oldPos.x = $selected.x;
    oldPos.y = $selected.y;
    oldPos.z = $selected.z;
    oldPos.pattern = $selected.pattern;
    $infoText = "";
  }

  function drawPreview(selected) {

    blockLayer.removeChild(blockLayer.getChildByName("blockPreview"));
    bannerLayer.removeChild(bannerLayer.getChildByName("basePreview"));
    bannerLayer.removeChild(bannerLayer.getChildByName("patternPreview"));

    if($drawingMode.block) {
      const blockBasePreview = new PIXI.Sprite(blockTextures[selected.block].blocktexture)
      blockBasePreview.width = 64;
      blockBasePreview.height = 64;
      blockBasePreview.position.set(selected.x * 64, selected.y * 64);
      blockBasePreview.parentLayer = blockLayer;
      blockBasePreview.zOrder = 2;
      blockBasePreview.name = "blockPreview"
      blockLayer.addChild(blockBasePreview);
    }

    if($drawingMode.base) {
      const baseIndex = selected.base * 41;
      const bannerBasePreview = new PIXI.Sprite(bannerTextures[baseIndex].texture)
      bannerBasePreview.width = 54;
      bannerBasePreview.height = 108;
      bannerBasePreview.position.set(selected.x * 64 + 5, selected.y * 64 + 8);
      bannerBasePreview.parentLayer = bannerLayer;
      bannerBasePreview.zOrder = -(selected.y * 7);
      bannerBasePreview.name = "basePreview"
      bannerLayer.addChild(bannerBasePreview);
    }

    if(($drawingMode.pattern && $currentBase) || ($drawingMode.base && $drawingMode.pattern)) {
      if(selected.pattern === 0) return;
      const bannerIndex = Number(selected.pattern) + Number(selected.color * 41);
      const bannerPreview = new PIXI.Sprite(bannerTextures[bannerIndex].texture)
      bannerPreview.width = 54;
      bannerPreview.height = 108;
      bannerPreview.zIndex = ($grid.height * 10) - (selected.y * 10) + 1;
      bannerPreview.position.set(selected.x * 64 + 5, selected.y * 64 + 8);
      bannerPreview.parentLayer = bannerLayer;
      bannerPreview.zOrder = -(selected.y * 7 - 1 - selected.z);
      bannerPreview.name = "patternPreview"
      bannerLayer.addChild(bannerPreview);
    }
  }

  function handleMousemove(event) {
    if(!canvasReady) return;
    if($drawingMode.pattern || $drawingMode.base || $drawingMode.block) {
      const mousePos = viewport.toWorld(app.renderer.plugins.interaction.mouse.global);
      $selected.x = Math.min(Math.max(parseInt(mousePos.x / 64), 0), $grid.width - 1);
      $selected.y = Math.min(Math.max(parseInt(mousePos.y / 64), 0), $grid.height - 1);
    }
  }

  function handleClick(event) {
    if($zDown) return;

    const banner = { ...$selected };
    if($drawingMode.block) {
      handleBlock(banner);
    }
    if($drawingMode.base) {
      handleBase(banner);
    }
    if($drawingMode.pattern) {
      if(!$currentBase && !$drawingMode.base) {
        $infoText = "Can't draw pattern on empty space without banner drawing mode turned on."
        return;
      } 
      handleBanner(banner);
    }
    if(!$drawingMode.pattern && !$drawingMode.base && !$drawingMode.block) {
      const mousePos = viewport.toWorld(app.renderer.plugins.interaction.mouse.global);
      $selected.x = Math.min(Math.max(parseInt(mousePos.x / 64), 0), $grid.width - 1);
      $selected.y = Math.min(Math.max(parseInt(mousePos.y / 64), 0), $grid.height - 1);
    }
     //trigger to update currentBanner
     $selected.x = $selected.x;
  }

  function handleRightClick(event) {
    if($drawingMode.block && $currentBlock && !$drawingMode.pattern && !$drawingMode.base) {
      $selected.block = $currentBlock.block
    }
    if($drawingMode.base && $currentBase && !$drawingMode.pattern) {
      $selected.base = $currentBase.base
    }
    if($drawingMode.pattern && $currentBanner) {
      $selected.pattern = $currentBanner.pattern
      $selected.color = $currentBanner.color
    }
    //trigger to update currentBanner
    $selected.x = $selected.x;
  }
  
  function handleCopy() {
    copiedBanner.block = null;
    copiedBanner.base = null;
    copiedBanner.patterns = [];
    if($currentBlock) {
      copiedBanner.block = $currentBlock.block
    }
    if($currentBase) {
      copiedBanner.base = $currentBase.base
    }
    for(let i = 0; i < 6; i++) {
      if($grid.banners.has($selected.x + '|' + $selected.y + '|' + i)) {
        copiedBanner.patterns[i] = {pattern: $grid.banners.get($selected.x + '|' + $selected.y + '|' + i).pattern, color: $grid.banners.get($selected.x + '|' + $selected.y + '|' + i).color}
      }
    }
  }

  function handlePaste() {
    if(copiedBanner.block !== null) {
      let block = { ...$selected };
      block.block = copiedBanner.block;
      handleBlock(block);
    }
    if(copiedBanner.base !== null) {
      let base = { ...$selected };
      base.base = copiedBanner.base;
      handleBase(base);
    }

    for(let i = 0; i < 6; i++) {
      if($grid.banners.has($selected.x + '|' + $selected.y + '|' + i)) {
        if(!!copiedBanner.patterns[i]) {
          const oldPattern = $grid.banners.get($selected.x + '|' + $selected.y + '|' + i);
          let pattern = {...oldPattern};
          pattern.z = i;
          pattern.pattern = copiedBanner.patterns[i].pattern;
          pattern.color = copiedBanner.patterns[i].color;
          commands.execute('changeBanner', {selected: pattern, oldBanner: oldPattern});
        } else {
          let pattern = $grid.banners.get($selected.x + '|' + $selected.y + '|' + i)
          commands.execute('removePattern', pattern);
        }
      } else {
        if(!!copiedBanner.patterns[i]) {
          let pattern = { ...$selected };
          pattern.z = i;
          pattern.pattern = copiedBanner.patterns[i].pattern;
          pattern.color = copiedBanner.patterns[i].color;
          commands.execute('insertPattern', pattern);
        }
      }
    }
  }

  function swapLayers(layer1, layer2) {

    const oldPattern1 = $grid.banners.get($selected.x + '|' + $selected.y + '|' + layer1);
    const oldPattern2 = $grid.banners.get($selected.x + '|' + $selected.y + '|' + layer2);
    
    if(!!oldPattern2) {
      handleBanner({
        x: $selected.x,
        y: $selected.y,
        z: layer1,
        base: oldPattern2.base,
        pattern: oldPattern2.pattern,
        color: oldPattern2.color,
        block: oldPattern2.block
      });
    } else {
      if($grid.banners.get($selected.x + '|' + $selected.y + '|' + layer1)) {
        commands.execute('removePattern', oldPattern1);
      }
    }

    if(!!oldPattern1) {
      handleBanner({
        x: $selected.x,
        y: $selected.y,
        z: layer2,
        base: oldPattern1.base,
        pattern: oldPattern1.pattern,
        color: oldPattern1.color,
        block: oldPattern1.block
      });
    } else {
      if($grid.banners.get($selected.x + '|' + $selected.y + '|' + layer2)) {
        commands.execute('removePattern', oldPattern2);
      }
    }

    //trigger update
    $selected.x = $selected.x;

  }

  const actions = {
    insertPattern: {
      execute(selected) {
        addNewPattern(selected);
      },
      undo(pattern) {
        removePattern(pattern);
      },
      redo(pattern) {
        addNewPattern(pattern);
      }
    },
    changeBanner: {
      execute({selected, oldBanner}) {
        changePattern({selected, oldBanner});
      },
      undo({selected, oldBanner}) {
        undoChangePattern({selected, oldBanner});
      },
      redo({selected, oldBanner}) {
        changePattern({selected, oldBanner});
      }
    },
    removePattern: {
      execute(selected) {
        removePattern(selected);
      },
      undo(pattern) {
        addNewPattern(pattern);
      },
      redo(pattern) {
        removePattern(pattern);
      }
    },
    insertBlock: {
      execute(selected) {
        addNewBlock(selected);
      },
      undo(pattern) {
        removeBlock(pattern);
      },
      redo(pattern) {
        addNewBlock(pattern);
      }
    },
    changeBlock: {
      execute({selected, oldBlock}) {
        changeBlock({selected, oldBlock});
      },
      undo({selected, oldBlock}) {
        undoChangeBlock({selected, oldBlock});
      },
      redo({selected, oldBlock}) {
        changeBlock({selected, oldBlock});
      }
    },
    removeBlock: {
      execute(selected) { 
        removeBlock(selected);
      },
      undo(pattern) {
        addNewBlock(pattern);
      },
      redo(pattern) {
        removeBlock(pattern);
      }
    },
    insertBase: {
      execute(selected) {
        addNewBase(selected);
      },
      undo(pattern) {
        removeBase(pattern);
      },
      redo(pattern) {
        addNewBase(pattern);
      }
    },
    changeBase: {
      execute({selected, oldBase}) {
        changeBase({selected, oldBase});
      },
      undo({selected, oldBase}) {
        undoChangeBase({selected, oldBase});
      },
      redo({selected, oldBase}) {
        changeBase({selected, oldBase});
      }
    },
    removeBase: {
      execute(selected) { 
        removeBase(selected);
      },
      undo(pattern) {
        addNewBase(pattern);
      },
      redo(pattern) {
        removeBase(pattern);
      }
    },
    adjustGridSize: {
      execute({old_w,old_h, w, h}) { 
        adjustGridSize({old_w,old_h, w, h});
      },
      undo({old_w,old_h, w, h}) {
        undoAdjustGridSize({old_w,old_h, w, h});
      },
      redo({old_w,old_h, w, h}) {
        adjustGridSize({old_w,old_h, w, h});
      }
    }
  }

	export const commands = commandStore(initialCommands, actions);

	function handleBanner(selected) {
    if(selected.pattern === 0) return;
    if($grid.banners.has(selected.x + '|' + selected.y + '|' + selected.z)) {
      const oldBanner = $grid.banners.get(selected.x + '|' + selected.y + '|' + selected.z)
      if((selected.z + '|' + selected.pattern + '|' + selected.color) !== (oldBanner.z + '|' + oldBanner.pattern + '|' + oldBanner.color)) {
        commands.execute('changeBanner', {selected, oldBanner});
      }
    } else {
      commands.execute('insertPattern', selected);
    }
	}

  function handleBlock(selected) {
    if($currentBlock) {
      const oldBlock = { ...$currentBlock }
      if(oldBlock.block !== selected.block) {
        commands.execute('changeBlock', {selected, oldBlock});
      }
    } else {
      commands.execute('insertBlock', selected);
    }
  }

	function handleBase(selected) {
    if($currentBase) {
      const oldBase = { ...$currentBase }
      if(oldBase.base !== selected.base) {
        commands.execute('changeBase', {selected, oldBase});
      }
    } else {
      commands.execute('insertBase', selected);
    }
  }

  function handleDelete() {
    if($drawingMode.block && $currentBlock) {
      const block = { ...$currentBlock};
      commands.execute('removeBlock', block);
    }
    if($drawingMode.base && $currentBase) {
      if($grid.banners.has($selected.x + '|' + $selected.y + '|' + $selected.z)) {
        commands.execute('removePattern', $grid.banners.get($selected.x + '|' + $selected.y + '|' + $selected.z));
      } else {
        const base = { ...$currentBase };  
        commands.execute('removeBase', base); 
      }
    }
    if($drawingMode.pattern && $currentBanner && !$drawingMode.base) {
      const banner = { ...$currentBanner};  
      commands.execute('removePattern', banner);
    }
    $selected.x = $selected.x
  }

  function handleDeleteBanner() {
    if($currentBase) {
      for(let i = 6; i >= 0; i--){  
        if($grid.banners.has($selected.x + '|' + $selected.y + '|' + i)) {
          commands.execute('removePattern', $grid.banners.get($selected.x + '|' + $selected.y + '|' + i));
        }
      }
      const base = { ...$currentBase };  
      commands.execute('removeBase', base);
    }
    if($currentBlock) {
      const block = { ...$currentBlock };
      commands.execute('removeBlock', block);
    }
    $selected.x = $selected.x
  }

  /* 
  WHAT: Draw banner on canvas
  */

  function addNewBlock(selected) {
    const block = new PIXI.Sprite(blockTextures[selected.block].blocktexture);
    block.width = 64;
    block.height = 64;
    block.position.set(selected.x * 64, selected.y * 64);
    block.parentLayer = blockLayer;
    block.zOrder = 1;
    block.name = 'bl|' + selected.x + '|' + selected.y;
    blockLayer.addChild(block);
    $grid.banners.set('bl|' + selected.x + '|' + selected.y, {type: 0, x: selected.x, y:selected.y, block:selected.block});
  }

  function changeBlock({selected, oldBlock}) {
    const block = blockLayer.getChildByName('bl|' + oldBlock.x + '|' + oldBlock.y);
    block.texture = blockTextures[selected.block].blocktexture;
    $grid.banners.set('bl|' + selected.x + '|' + selected.y, {type: 0, x: selected.x, y:selected.y, block:selected.block});
  }

  function removeBlock(pattern) {
    const oldBlock = blockLayer.getChildByName('bl|' + pattern.x + '|' + pattern.y);
    blockLayer.removeChild(oldBlock);
    $grid.banners.delete('bl|' + pattern.x + '|' + pattern.y);
  }

  function undoChangeBlock({selected, oldBlock}) {
    const block = blockLayer.getChildByName('bl|' + selected.x + '|' + selected.y);
    block.texture = blockTextures[oldBlock.block].blocktexture;
    $grid.banners.set('bl|' + oldBlock.x + '|' + oldBlock.y, oldBlock);
  }

  function addNewBase(selected) {
    const base = new PIXI.Sprite(bannerTextures[selected.base * 41].texture)
    base.width = 54;
    base.height = 108;
    base.position.set(selected.x * 64 + 5, selected.y * 64 + 8);
    base.parentLayer = bannerLayer;
    base.zOrder = -(selected.y * 7);
    base.name = selected.x + '|' + selected.y
    bannerLayer.addChild(base);
    $grid.banners.set(selected.x + '|' + selected.y, {type: 1, x: selected.x, y:selected.y, base:selected.base});
  }
  
  function changeBase({selected, oldBase}) {
    const base = bannerLayer.getChildByName(oldBase.x + '|' + oldBase.y);
    base.texture = bannerTextures[selected.base * 41].texture;
    $grid.banners.set(selected.x + '|' + selected.y, {type: 1, x: selected.x, y:selected.y, base:selected.base});
  }
  
  function removeBase(pattern) {
    const oldBase = bannerLayer.getChildByName(pattern.x + '|' + pattern.y);
    bannerLayer.removeChild(oldBase)    
    $grid.banners.delete(pattern.x + '|' + pattern.y);
  }
  
  function undoChangeBase({selected, oldBase}) {
    const base = bannerLayer.getChildByName(selected.x + '|' + selected.y);
    base.texture = bannerTextures[oldBase.base * 41].texture;
    $grid.banners.set(oldBase.x + '|' + oldBase.y, oldBase);
  }

  function addNewPattern(selected) {
    const bannerIndex = Number(selected.pattern) + Number(selected.color * 41);
    const banner = new PIXI.Sprite(bannerTextures[bannerIndex].texture)
    banner.width = 54;
    banner.height = 108;
    banner.zIndex = ($grid.height * 10) - (selected.y * 10) + 1;
    banner.position.set(selected.x * 64 + 5, selected.y * 64 + 8);
    banner.parentLayer = bannerLayer;
    banner.zOrder = -(selected.y * 7 - 1 - selected.z);
    banner.name = selected.x + '|' + selected.y + '|' + selected.z
    bannerLayer.addChild(banner);
    $grid.banners.set(selected.x + '|' + selected.y + '|' + selected.z, {type: 2, x: selected.x, y:selected.y, z:selected.z, pattern:selected.pattern, color:selected.color});
  }
  
  function changePattern({selected, oldBanner}) {
    const oldPattern = bannerLayer.getChildByName(oldBanner.x + '|' + oldBanner.y + '|' + oldBanner.z);
    const bannerIndex = Number(selected.pattern) + Number(selected.color * 41);
    oldPattern.texture = bannerTextures[bannerIndex].texture;
    $grid.banners.set(selected.x + '|' + selected.y + '|' + selected.z, {type: 2, x: selected.x, y:selected.y, z:selected.z, pattern:selected.pattern, color:selected.color});
  }
  
  function removePattern(pattern) {
    const oldBanner = bannerLayer.getChildByName(pattern.x + '|' + pattern.y + '|' + pattern.z);
    bannerLayer.removeChild(oldBanner);
    $grid.banners.delete(pattern.x + '|' + pattern.y + '|' + pattern.z);
  }

  function undoChangePattern({selected, oldBanner}) {
    const oldPattern = bannerLayer.getChildByName(selected.x + '|' + selected.y + '|' + selected.z);
    const bannerIndex = Number(oldBanner.pattern) + Number(oldBanner.color * 41);
    oldPattern.texture = bannerTextures[bannerIndex].texture;
    $grid.banners.set(oldBanner.x + '|' + oldBanner.y + '|' + oldBanner.z, oldBanner);
  }

  export function doTimelapse(mode) {
    if(!$timelapseRunning && mode === "run") {
      commands.timelapse(100);
    }
    if($timelapseRunning && mode === "stop") {
      commands.stopTimelapse()
    }
  }

  export function handleGridSize(x_amount, y_amount) {
    const currentWidth = $grid.width;
    const currentHeight = $grid.height;
    deleteBannersOnGridSizeChange(x_amount, y_amount, currentWidth, currentHeight)
    commands.execute('adjustGridSize', {
      old_w: currentWidth, 
      old_h: currentHeight, 
      w: currentWidth + x_amount, 
      h: currentHeight + y_amount
    });
  }

  function adjustGridSize({old_w,old_h, w, h}) {
    $grid.width = w;
    $grid.height = h;
  }

  function undoAdjustGridSize({old_w,old_h, w, h}) {
    $grid.width = old_w;
    $grid.height = old_h;
  }

  function deleteBannersOnGridSizeChange(x_amount, y_amount, currentWidth, currentHeight) {
    if(x_amount < 0) {
      for(let i = 0; i < $grid.height; i++) {
        for(let j = 6; j >= 0; j--) {  
          if($grid.banners.has((currentWidth-1) + '|' + i + '|' + j)) {
            commands.execute('removePattern', $grid.banners.get((currentWidth-1) + '|' + i + '|' + j));
          }
        }
        if($grid.banners.has((currentWidth-1) + '|' + i)) {
          commands.execute('removeBase',  $grid.banners.get((currentWidth-1) + '|' + i));
        }
        if($grid.banners.has('bl|' + (currentWidth-1) + '|' + i)) {
          commands.execute('removeBlock', $grid.banners.get('bl|' + (currentWidth-1) + '|' + i));
        }
      }
    }

    if(y_amount < 0) {
      for(let i = 0; i < currentWidth; i++) {
        for(let j = 6; j >= 0; j--) {  
          if($grid.banners.has(i + '|' + (currentHeight-1) + '|' + j)) {
            commands.execute('removePattern', $grid.banners.get(i + '|' + (currentHeight-1) + '|' + j));
          }
        }
        if($grid.banners.has(i + '|' + (currentHeight-1))) {
          commands.execute('removeBase',  $grid.banners.get(i + '|' + (currentHeight-1)));
        }
        if($grid.banners.has('bl|' + i + '|' + (currentHeight-1))) {
          commands.execute('removeBlock', $grid.banners.get('bl|' + i + '|' + (currentHeight-1)));
        }
      }
    }

  }

  $: if(canvasReady) {
    urlParams = new URLSearchParams(document.location.search);
    if(urlParams) {
      if(urlParams.get("w")) $grid.width = Number(urlParams.get("w"));
      if(urlParams.get("h")) $grid.height = Number(urlParams.get("h"));
      const loadedUrl = urlParams.get("c");
      const decompressed = LZString.decompressFromEncodedURIComponent(loadedUrl);
      if(decompressed) { 
        loadGridFromURL(JSON.parse(decompressed) );
      };
    }
  }

  const loadGridFromURL = (data) => {
    for(let i = 0; i < data.length; i++) {
      if(data[i][0] === 0) {
        const [type,x,y,block] = data[i];
        const blockObj = {x,y,block}
        addNewBlock(blockObj)
      }
      if(data[i][0] === 1) {
        const [type,x,y,base] = data[i];
        const baseObj = {x,y,base}
        addNewBase(baseObj)
      }
      if(data[i][0] === 2) {
        const [type, x,y,z,pattern,color] = data[i];
        const patternObj = {x,y,z,pattern,color}
        addNewPattern(patternObj)
      }
    }
  }

  const saveGridToURL = (grid) => {
    if(!canvasReady) return
    /* 
    WHAT: Turn grid banners map object into a simple array
    WHY: saves a lot of space!
    */
    const gridArr = [];
    for (const banner of grid.banners.values()) {
      let bannerArr = Object.keys(banner).map((key) => banner[key]);
      gridArr.push(bannerArr);
    }
    //compress array with LZString lib
    compressedCommands = LZString.compressToEncodedURIComponent(JSON.stringify(gridArr));
    url.searchParams.set("w", grid.width);
    url.searchParams.set("h", grid.height);
    url.searchParams.set("c", compressedCommands);
    history.replaceState({}, '', url);
  }

  $: $commands, saveGridToURL($grid);

  export function handleExportAsPng() {
    if(uiLayer) uiLayer.visible = false;
    if($drawingMode.block) blockLayer.getChildByName("blockPreview").visible = false;
    if($drawingMode.base) bannerLayer.getChildByName("basePreview").visible = false;
    if(!!$currentBanner && $drawingMode.pattern) {
      bannerLayer.getChildByName("patternPreview").visible = false;
    }

    app.renderer.plugins.extract.canvas(viewport).toBlob(async function(b) {
      await fileSave(b, {
          fileName: `Bannermode_Image_${(new Date()).toISOString().slice(0,10)}.png`,
          extensions: ['.png'],
      });
    });
    if(uiLayer) uiLayer.visible = true;
  }
  
  export async function saveAsFile() {
    if(!canvasReady) return;
    const gridArr = [];
    for (const banner of $grid.banners.values()) {
      let bannerArr = Object.keys(banner).map((key) => banner[key]);
      gridArr.push(bannerArr);
    }
    const compressedGrid = LZString.compressToEncodedURIComponent(JSON.stringify(gridArr));
    const file = `
      <!DOCTYPE HTML>
      <html>
        <head>
          <title>Link to my Bannermode creation</title>\
          <meta http-equiv="refresh" content="0; url=https://bannermo.de/?w=${$grid.width}&h=${$grid.height}&c=${compressedGrid}" />\
        </head>
        <body>
          <h1>Opening Bannermode...</h1>
          <h4>If the file doesn't open automatically, click the link:</h4>
          <p><a href="https://bannermo.de/?w=${$grid.width}&h=${$grid.height}&c=${compressedGrid}">Open your Bannermode drawing</a></p>
        </body>
      </html>
    `
    const blob = new Blob([file], { type: 'text/html'});
    await fileSave(blob, {
      fileName: `Bannermode_Link_${(new Date()).toISOString().slice(0,10)}.html`,
      extensions: ['.html'],
    });
  }
  export async function loadFromFile() {
    if(!canvasReady) return;
    const options = {
      mimeTypes: ['text/html'],
      extensions: ['.html'],
      multiple: false,
      description: 'Bannermode link file',
      startIn: 'downloads',
    };
    const blob = await fileOpen(options);
    function loadFile() {
      blob.text().then(text => {
          return new DOMParser().parseFromString(text, "text/html").querySelector('a').getAttribute("href")
      }).then(urlString => {
        let urlParams = (new URL(urlString)).searchParams;
        //empty canvas
        $grid.banners.clear()
        while(blockLayer.children[0]) { 
          blockLayer.removeChild(blockLayer.children[0]);
        }
        while(bannerLayer.children[0]) { 
          bannerLayer.removeChild(bannerLayer.children[0]);
        }
        if(urlParams.get("w")) $grid.width = parseInt(urlParams.get("w"));
        if(urlParams.get("h")) $grid.height = parseInt(urlParams.get("h"));
        const loadedUrl = urlParams.get("c");
        const decompressed = LZString.decompressFromEncodedURIComponent(loadedUrl);
        if(decompressed) { 
          loadGridFromURL(JSON.parse(decompressed) );
        };
      });

    }
    loadFile();
  }
</script>

<svelte:window 

  use:shortcut={{code: 'KeyQ', callback: () => handleClick()}}
  use:shortcut={{code: 'Digit1', callback: () => $selected.z = 0}}
  use:shortcut={{code: 'Digit2', callback: () => $selected.z = 1}}
  use:shortcut={{code: 'Digit3', callback: () => $selected.z = 2}}
  use:shortcut={{code: 'Digit4', callback: () => $selected.z = 3}}
  use:shortcut={{code: 'Digit5', callback: () => $selected.z = 4}}
  use:shortcut={{code: 'Digit6', callback: () => $selected.z = 5}}
  use:shortcut={{shift: true, code: 'Space', callback: () => handleDeleteBanner()}}
  use:shortcut={{code: 'Space', callback: () => handleDelete()}}
  use:shortcut={{control: true, code: 'KeyZ', callback: () => commands.undo()}}
  use:shortcut={{control: true, shift: true, code: 'KeyZ', callback: () => commands.redo()}}
  use:shortcut={{control: true, code: 'KeyC', callback: () => handleCopy()}}
  use:shortcut={{control: true, code: 'KeyV', callback: () => handlePaste()}}
  use:shortcut={{control: true, code: 'KeyE', callback: () => handleExportAsPng()}}
  use:shortcut={{control: true, shift: true, code: 'KeyS', callback: () => saveAsFile()}}

  use:shortcut={{code: 'KeyB', callback: () => $drawingMode.block = !$drawingMode.block}}
  use:shortcut={{code: 'KeyN', callback: () => $drawingMode.base = !$drawingMode.base}}
  use:shortcut={{code: 'KeyM', callback: () => $drawingMode.pattern = !$drawingMode.pattern}}

  use:shortcut={{shift: true, code: 'Digit1', callback: () => swapLayers(0,1)}}
  use:shortcut={{shift: true, code: 'Digit2', callback: () => swapLayers(1,2)}}
  use:shortcut={{shift: true, code: 'Digit3', callback: () => swapLayers(2,3)}}
  use:shortcut={{shift: true, code: 'Digit4', callback: () => swapLayers(3,4)}}
  use:shortcut={{shift: true, code: 'Digit5', callback: () => swapLayers(4,5)}}

/>

<div class="content" bind:clientWidth={canvasWidth} bind:clientHeight={canvasHeight}>
  <canvas 
    bind:this={view} 
    on:mousemove={handleMousemove}
    on:click={handleClick}
    on:contextmenu|preventDefault={handleRightClick}
    />
</div>

<style>
  .content {
    width: 100%;
    height: 100%;
  }
  canvas {
    width: 100%;
    height: 100%;
    image-rendering: pixelated;
    background-color:burlywood;
  }
</style>  